import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { NetworkService } from "./network.service";

/** Maximum allowed clock skew (5 minutes, matching AWS Cognito tolerance). */
const MAX_SKEW_MS = 5 * 60 * 1000;

/** How often to re-check clock sync (ms). */
const CHECK_INTERVAL_MS = 60_000;

@Injectable()
export class ClockSyncService implements OnDestroy {

    private _synced$ = new BehaviorSubject<boolean>(true);
    private intervalId: ReturnType<typeof setInterval> | null = null;

    constructor(private network: NetworkService) {
        this.check();
        this.intervalId = setInterval(() => this.check(), CHECK_INTERVAL_MS);

        // Re-check immediately when coming back online
        this.network.getNetworkStatus().subscribe(online => {
            if (online) this.check();
        });
    }

    ngOnDestroy(): void {
        if (this.intervalId) clearInterval(this.intervalId);
    }

    /** Whether the local clock is within acceptable skew of the server. */
    isClockSynced(): Observable<boolean> {
        return this._synced$.asObservable();
    }

    /** Force a re-check (e.g. from a "retry" button). */
    recheck(): void {
        this.check();
    }

    private async check(): Promise<void> {
        try {
            const before = Date.now();
            const response = await fetch(window.location.origin, { method: 'HEAD', cache: 'no-store' });
            const after = Date.now();
            const serverDateStr = response.headers.get('Date');
            if (!serverDateStr) return;

            const serverTime = new Date(serverDateStr).getTime();
            // Account for network round-trip by using the midpoint
            const localTime = (before + after) / 2;
            const skew = Math.abs(serverTime - localTime);

            this._synced$.next(skew < MAX_SKEW_MS);
        } catch {
            // Network error — clock sync status is unknown; leave current value.
            // Offline state is handled separately by NetworkService.
        }
    }
}
